import React, { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { Loader2, AlertCircle, Clock, CheckCircle2, AlertTriangle, TrendingUp, Building2, Users, Award, Printer, Settings, X, Sparkles, Search, Filter, FileCheck2, RefreshCw } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { useAuth } from '../../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = { pending: '#eab308', in_progress: '#2563eb', resolved: '#16a34a', escalated: '#dc2626' };
const STATUS_LABELS = { pending: 'معلَّق', in_progress: 'قيد المعالجة', resolved: 'محلول', escalated: 'مُصعَّد' };

function Kpi({ label, value, icon: Icon, color = 'purple', suffix = '', hint = null }) {
  const colors = {
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
  };
  return (
    <div className={`rounded-xl p-3 border-2 ${colors[color]}`}>
      <div className="flex items-center justify-between mb-1">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <span className="text-xs opacity-80">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}{suffix && <span className="text-xs mr-1 opacity-70">{suffix}</span>}</div>
      {hint && <div className="text-[10px] mt-1 opacity-70 leading-tight">{hint}</div>}
    </div>
  );
}

function TopList({ title, icon: Icon, items = [], color = 'purple' }) {
  const ranks = ['bg-yellow-400', 'bg-gray-300', 'bg-orange-400', 'bg-blue-300', 'bg-purple-300', 'bg-indigo-300', 'bg-pink-300', 'bg-green-300', 'bg-gray-200', 'bg-gray-100'];
  return (
    <div className="bg-white border rounded-xl p-4">
      <h3 className="font-bold text-sm mb-3 flex items-center gap-2 text-gray-700"><Icon className="w-4 h-4" /> {title}</h3>
      {items?.length ? (
        <ol className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <span className={`w-6 h-6 ${ranks[i] || 'bg-gray-100'} text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0`}>{i + 1}</span>
              <div className="flex-1 min-w-0 truncate font-medium">{it.name}</div>
              <span className={`text-${color}-700 font-bold`}>{it.count}</span>
            </li>
          ))}
        </ol>
      ) : <p className="text-center text-gray-400 py-4 text-sm">لا توجد بيانات بعد</p>}
    </div>
  );
}

function SettingsModal({ token, current, onClose, onSaved }) {
  const [days, setDays] = useState(current || 5);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const d = parseInt(days, 10);
    if (!d || d < 1 || d > 365) { toast.error('أدخل قيمة بين 1 و 365'); return; }
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/invoice-matching/receipt-actions/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ overdue_threshold_days: d }),
      });
      if (!res.ok) {
        if (res.status === 403) toast.error('هذا الإعداد للأدمن فقط');
        else toast.error('فشل الحفظ');
        return;
      }
      // إعادة تشغيل الكشف التلقائي مباشرة بعد تغيير الإعداد
      const r2 = await fetch(`${API_URL}/api/invoice-matching/receipt-actions/auto-detect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const j2 = r2.ok ? await r2.json() : { created: 0 };
      toast.success(`تم الحفظ — أُنشئ ${j2.created || 0} إجراء جديد تلقائياً`);
      onSaved();
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-testid="settings-modal">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-2"><Settings className="w-5 h-5" /> إعدادات نظام الإجراءات</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">عتبة الكشف التلقائي (بالأيام)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="1"
                max="365"
                value={days}
                onChange={e => setDays(e.target.value)}
                data-testid="threshold-days-input"
                className="flex-1 h-11 px-3 border-2 border-gray-200 focus:border-indigo-500 rounded-lg outline-none text-lg font-bold text-center"
              />
              <span className="text-sm font-bold text-gray-600">يوم</span>
            </div>
            <p className="text-xs text-gray-500 mt-2 leading-relaxed">
              عند تشغيل تقرير الإجراءات، يقوم النظام بفحص جميع الاستلامات المعتمدة بدون فواتير
              ويُنشئ <strong>تلقائياً</strong> إجراءً معلَّقاً لكل استلام مرّ عليه أكثر من العدد المحدد من تاريخ الاعتماد.
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            <div className="flex items-center gap-1.5 font-bold mb-1"><Sparkles className="w-3.5 h-3.5" /> ملاحظة</div>
            عند تغيير القيمة، يُعاد التشغيل فوراً وقد يتم إنشاء إجراءات جديدة. لا يتم حذف الإجراءات الموجودة.
          </div>
        </div>
        <div className="border-t p-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-gray-50">إلغاء</button>
          <button onClick={save} disabled={saving} data-testid="save-settings" className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-lg text-sm font-bold flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
            حفظ الإعدادات
          </button>
        </div>
      </div>
    </div>
  );
}

export function ActionsReportTab({ token }) {
  const { user } = useAuth();
  const isAdmin = user && (user.role === 'super_admin' || user.role === 'company_admin');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [deliveryModal, setDeliveryModal] = useState(null); // {delivery_id, receipt_number}
  
  const handleForceRefresh = async () => {
    try {
      setRefreshing(true);
      const res = await fetch(`${API_URL}/api/invoice-matching/receipt-actions/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 403) toast.error('هذا الإجراء للأدمن فقط');
        else toast.error('فشل التحديث');
        return;
      }
      const j = await res.json();
      toast.success(`تم التحديث — أُنشئ ${j.created || 0} إجراء جديد، حُلَّ ${j.auto_resolved || 0} تلقائياً`);
      setReloadKey(k => k + 1);
    } finally {
      setRefreshing(false);
    }
  };
  
  // Filters & view mode
  const [viewMode, setViewMode] = useState('overdue'); // 'overdue' | 'resolved'
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'auto' | 'manual'
  const [minDays, setMinDays] = useState('');
  
  const printRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/invoice-matching/receipt-actions/stats`, {
      headers: { 'Authorization': `Bearer ${token}` }
    }).then(r => r.ok ? r.json() : null).then(d => {
      if (!cancelled) { setData(d); setLoading(false); }
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, reloadKey]);

  const handlePrint = () => {
    const today = new Date().toLocaleDateString('ar-EG-u-nu-latn', { year: 'numeric', month: '2-digit', day: '2-digit' });
    const k = data.kpis;
    const isResolved = viewMode === 'resolved';
    const reportTitle = isResolved ? 'تقرير الإجراءات المُحلَّة (استلامات صدرت فواتيرها)' : 'تقرير إجراءات الاستلامات المتأخرة';
    const colHeaders = isResolved
      ? ['#', 'رقم الاستلام', 'المورد', 'الحالة', 'المتابع', 'أيام التأخير', 'ملاحظات', 'رقم الفاتورة', 'آخر بند Timeline']
      : ['#', 'رقم الاستلام', 'المورد', 'الحالة', 'المتابع', 'أيام التأخير', 'ملاحظات', 'آخر بند Timeline'];
    const filterDesc = [];
    if (search) filterDesc.push(`بحث: "${search}"`);
    if (statusFilter !== 'all') filterDesc.push(`حالة: ${STATUS_LABELS[statusFilter] || statusFilter}`);
    if (sourceFilter === 'auto') filterDesc.push('المصدر: تلقائي');
    if (sourceFilter === 'manual') filterDesc.push('المصدر: يدوي');
    if (minDays) filterDesc.push(`تأخير ≥ ${minDays} يوم`);
    
    const rows = filteredList.map((it, i) => {
      const days = it.days_delayed != null ? it.days_delayed : it.days_open;
      const dayColor = days >= 14 ? '#dc2626' : days >= 7 ? '#ea580c' : '#ca8a04';
      const lh = it.last_history;
      const lhText = lh ? `<div style="font-size:11px"><div>${(lh.text || '—').replace(/</g, '&lt;')}</div><div style="color:#6b7280;font-size:10px">${lh.by} — ${String(lh.at || '').slice(0, 16).replace('T', ' ')}</div></div>` : '<span style="color:#9ca3af">—</span>';
      const invCell = isResolved ? `<td style="padding:6px;border:1px solid #ddd;font-size:11px">${(it.linked_invoices || []).map(li => li.invoice_number).filter(Boolean).join(' / ') || '—'}</td>` : '';
      return `<tr>
        <td style="padding:6px;border:1px solid #ddd;text-align:center">${i + 1}</td>
        <td style="padding:6px;border:1px solid #ddd;font-weight:bold">${it.receipt_number || '—'}${it.auto_created ? ' <span style="background:#eef2ff;color:#4338ca;padding:1px 4px;border-radius:3px;font-size:9px">تلقائي</span>' : ''}</td>
        <td style="padding:6px;border:1px solid #ddd">${it.supplier_name || '—'}</td>
        <td style="padding:6px;border:1px solid #ddd"><span style="color:${STATUS_COLORS[it.status]};font-weight:bold">${STATUS_LABELS[it.status]}</span>${it.auto_resolved_by_linking ? ' <span style="background:#d1fae5;color:#065f46;padding:1px 4px;border-radius:3px;font-size:9px">ربط بفاتورة</span>' : ''}</td>
        <td style="padding:6px;border:1px solid #ddd;color:#7c3aed">${it.created_by_name || '—'}</td>
        <td style="padding:6px;border:1px solid #ddd;text-align:center"><span style="background:${dayColor};color:#fff;padding:2px 8px;border-radius:9999px;font-weight:bold">${days != null ? days : '—'}</span></td>
        <td style="padding:6px;border:1px solid #ddd;font-size:11px">${(it.notes || '—').replace(/</g, '&lt;')}</td>
        ${invCell}
        <td style="padding:6px;border:1px solid #ddd">${lhText}</td>
      </tr>`;
    }).join('');

    const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>${reportTitle}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; direction: rtl; color:#111827; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { font-size: 11px; color: #6b7280; margin-bottom: 6px; }
  .filters { font-size: 10px; color: #4b5563; margin-bottom: 8px; background:#f9fafb; padding:4px 8px; border-radius:6px; border:1px solid #e5e7eb; }
  .kpis { display:grid; grid-template-columns: repeat(8, 1fr); gap:6px; margin-bottom:12px; }
  .kpi { border:1px solid #e5e7eb; border-radius:8px; padding:6px; font-size:11px; text-align:center; }
  .kpi .v { font-size:18px; font-weight:bold; color:#7c3aed; }
  table { width:100%; border-collapse: collapse; font-size:12px; }
  th { background:#f3f4f6; padding:6px; border:1px solid #ddd; text-align:right; font-weight:bold; }
  tr { page-break-inside: avoid; }
  .footer { margin-top: 10px; font-size: 10px; color: #6b7280; text-align: center; }
</style></head><body>
<h1>${reportTitle}</h1>
<div class="meta">تاريخ التقرير: ${today} — عدد الصفوف: ${filteredList.length}</div>
${filterDesc.length ? `<div class="filters">الفلاتر المطبقة: ${filterDesc.join(' • ')}</div>` : ''}
<div class="kpis">
  <div class="kpi">إجمالي الإجراءات<div class="v">${k.total_actions}</div></div>
  <div class="kpi">معلَّق<div class="v" style="color:#ca8a04">${k.pending}</div></div>
  <div class="kpi">قيد المعالجة<div class="v" style="color:#2563eb">${k.in_progress}</div></div>
  <div class="kpi">محلول<div class="v" style="color:#16a34a">${k.resolved}</div></div>
  <div class="kpi">مُصعَّد<div class="v" style="color:#dc2626">${k.escalated}</div></div>
  <div class="kpi">نسبة الحل<div class="v">${k.resolution_rate}%</div></div>
  <div class="kpi">متوسط زمن الحل<div class="v">${k.avg_resolution_days} يوم</div></div>
  <div class="kpi">متأخر > 7 أيام<div class="v" style="color:#ea580c">${k.overdue_7_days}</div></div>
</div>
<table>
  <thead><tr>${colHeaders.map(h => `<th>${h}</th>`).join('')}</tr></thead>
  <tbody>${rows || `<tr><td colspan="${colHeaders.length}" style="text-align:center;padding:20px;color:#9ca3af">لا توجد بيانات تطابق الفلاتر</td></tr>`}</tbody>
</table>
<div class="footer">نظام إدارة المشاريع — تطوير محمد مهدي</div>
<script>window.onload = () => { setTimeout(() => { window.print(); }, 200); };</script>
</body></html>`;
    const w = window.open('', '_blank', 'width=1200,height=800');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  // Filtered list based on viewMode + search + filters
  const filteredList = useMemo(() => {
    if (!data) return [];
    const src = viewMode === 'resolved' ? (data.resolved_list || []) : (data.overdue_list || []);
    const q = search.trim().toLowerCase();
    const md = parseInt(minDays, 10);
    return src.filter(it => {
      if (q) {
        const hay = `${it.receipt_number || ''} ${it.supplier_name || ''} ${it.notes || ''} ${it.created_by_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'all' && it.status !== statusFilter) return false;
      if (sourceFilter === 'auto' && !it.auto_created) return false;
      if (sourceFilter === 'manual' && it.auto_created) return false;
      if (md && md > 0) {
        const d = it.days_delayed != null ? it.days_delayed : it.days_open;
        if (d == null || d < md) return false;
      }
      return true;
    });
  }, [data, viewMode, search, statusFilter, sourceFilter, minDays]);
  
  // طباعة إجراء فردي بكامل تفاصيله وسجل Timeline
  const printSingleAction = (it) => {
    const today = new Date().toLocaleDateString('ar-EG-u-nu-latn', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
    const statusLabel = STATUS_LABELS[it.status] || it.status;
    const statusColor = STATUS_COLORS[it.status] || '#6b7280';
    const STATUS_LBL = { pending: 'معلَّق', in_progress: 'قيد المعالجة', resolved: 'محلول', escalated: 'مُصعَّد' };
    const hist = [...(it.history || [])].sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
    const timelineRows = hist.length ? hist.map((h, i) => {
      const dt = String(h.at || '').slice(0, 16).replace('T', ' ');
      let detail = '';
      if (h.action === 'auto_resolve_by_linking') {
        detail = h.note || 'حُلَّ تلقائياً بربط فاتورة';
      } else {
        if (h.action === 'create' || h.action === 'auto_create') detail = h.action === 'auto_create' ? 'إنشاء تلقائي بواسطة النظام' : 'إنشاء الإجراء';
        if (h.status_to) {
          const lbl = STATUS_LBL[h.status_to] || h.status_to;
          const from = h.status_from ? ` (من ${STATUS_LBL[h.status_from] || h.status_from})` : '';
          detail += (detail ? ' — ' : '') + `تغيير الحالة إلى: ${lbl}${from}`;
        }
        if (h.notes_changed) detail += (detail ? ' — ' : '') + 'تعديل تفاصيل الإجراء';
        if (h.note) detail += (detail ? ' | ملاحظة: ' : '') + h.note;
      }
      return `<tr>
        <td style="padding:6px;border:1px solid #ddd;text-align:center;width:40px">${i + 1}</td>
        <td style="padding:6px;border:1px solid #ddd;white-space:nowrap;width:130px;color:#6b7280;font-size:11px">${dt}</td>
        <td style="padding:6px;border:1px solid #ddd;font-weight:bold;color:#7c3aed;width:140px">${(h.by_user_name || '—').replace(/</g, '&lt;')}</td>
        <td style="padding:6px;border:1px solid #ddd">${(detail || '—').replace(/</g, '&lt;')}</td>
      </tr>`;
    }).join('') : `<tr><td colspan="4" style="padding:14px;text-align:center;color:#9ca3af">لا توجد سجلات في Timeline</td></tr>`;
    
    const invoicesRow = (it.linked_invoices && it.linked_invoices.length) ? `
      <tr><td>الفواتير المربوطة</td><td>${it.linked_invoices.map(li => `<span style="background:#d1fae5;color:#065f46;padding:2px 8px;border-radius:4px;font-weight:bold;margin-left:4px">#${li.invoice_number || '—'}</span>`).join(' ')}</td></tr>
    ` : '';
    
    const html = `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>إجراء الاستلام #${it.receipt_number || ''}</title>
<style>
  @page { size: A4 portrait; margin: 14mm; }
  body { font-family: 'Segoe UI', Tahoma, Arial, sans-serif; direction: rtl; color:#111827; }
  h1 { font-size: 18px; margin: 0 0 4px; color:#7c3aed; }
  h2 { font-size: 14px; margin: 14px 0 6px; color:#4b5563; border-bottom:2px solid #e5e7eb; padding-bottom:4px; }
  .meta { font-size: 11px; color: #6b7280; margin-bottom: 10px; }
  .info { width:100%; border-collapse: collapse; font-size: 12px; margin-bottom:8px; }
  .info td { padding: 5px 8px; border:1px solid #e5e7eb; }
  .info td:first-child { background:#f9fafb; font-weight:bold; color:#374151; width: 28%; }
  .status-pill { display:inline-block; padding:3px 12px; border-radius:9999px; color:#fff; font-weight:bold; font-size:11px; background:${statusColor}; }
  .notes-box { border:1px solid #e5e7eb; border-radius:8px; padding:10px; background:#fefce8; font-size:13px; min-height:50px; line-height:1.7; white-space:pre-wrap; }
  table.tl { width:100%; border-collapse: collapse; font-size:12px; margin-top:6px; }
  table.tl th { background:#f3f4f6; padding:6px; border:1px solid #ddd; text-align:right; font-weight:bold; font-size:11px; }
  table.tl tr { page-break-inside: avoid; }
  .footer { margin-top:18px; padding-top:8px; border-top:1px solid #e5e7eb; font-size:10px; color:#6b7280; text-align:center; }
</style></head><body>
<h1>إجراء الاستلام #${it.receipt_number || ''}</h1>
<div class="meta">تاريخ طباعة التقرير: ${today}</div>

<h2>معلومات الاستلام والإجراء</h2>
<table class="info">
  <tr><td>رقم الاستلام</td><td><strong>${it.receipt_number || '—'}</strong>${it.auto_created ? ' <span style="background:#eef2ff;color:#4338ca;padding:1px 6px;border-radius:4px;font-size:10px">كشف تلقائي</span>' : ''}</td></tr>
  <tr><td>المورد</td><td><strong>${(it.supplier_name || '—').replace(/</g, '&lt;')}</strong></td></tr>
  <tr><td>تاريخ الاعتماد</td><td>${String(it.approved_at || '').slice(0, 10) || '—'}</td></tr>
  <tr><td>أيام التأخير (من تاريخ الاعتماد)</td><td><span style="background:#dc2626;color:#fff;padding:2px 10px;border-radius:9999px;font-weight:bold">${it.days_delayed != null ? it.days_delayed + ' يوم' : '—'}</span></td></tr>
  <tr><td>الحالة الحالية</td><td><span class="status-pill">${statusLabel}</span>${it.auto_resolved_by_linking ? ' <span style="background:#d1fae5;color:#065f46;padding:2px 8px;border-radius:4px;font-size:10px;margin-right:6px">حُلَّ بربط فاتورة</span>' : ''}</td></tr>
  <tr><td>المتابع (منشئ الإجراء)</td><td>${(it.created_by_name || '—').replace(/</g, '&lt;')}</td></tr>
  <tr><td>تاريخ إنشاء الإجراء</td><td>${String(it.created_at || '').slice(0, 16).replace('T', ' ') || '—'}</td></tr>
  ${it.resolved_by_name ? `<tr><td>حُلَّ بواسطة</td><td><strong>${it.resolved_by_name}</strong> — ${String(it.resolved_at || '').slice(0,16).replace('T',' ')}</td></tr>` : ''}
  ${invoicesRow}
</table>

<h3 style="font-size:13px;margin:10px 0 4px;color:#374151">السبب / المسؤول / تفاصيل الإجراء</h3>
<div class="notes-box">${(it.notes || '— لا توجد تفاصيل —').replace(/</g, '&lt;')}</div>

<h2>سجل الإجراءات (Timeline) — ${hist.length} إدخال</h2>
<table class="tl">
  <thead><tr>
    <th style="width:40px">#</th>
    <th style="width:130px">التاريخ والوقت</th>
    <th style="width:140px">المستخدم</th>
    <th>التفاصيل</th>
  </tr></thead>
  <tbody>${timelineRows}</tbody>
</table>

<div class="footer">نظام إدارة المشاريع — تطوير محمد مهدي</div>
<script>window.onload = () => { setTimeout(() => { window.print(); }, 200); };</script>
</body></html>`;
    const w = window.open('', '_blank', 'width=900,height=900');
    if (!w) { toast.error('السماح بالنوافذ المنبثقة مطلوب للطباعة'); return; }
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" /></div>;
  if (!data) return <div className="text-center py-12 text-gray-400">فشل تحميل البيانات</div>;

  const k = data.kpis;
  const pieData = [
    { name: STATUS_LABELS.pending, value: k.pending, color: STATUS_COLORS.pending },
    { name: STATUS_LABELS.in_progress, value: k.in_progress, color: STATUS_COLORS.in_progress },
    { name: STATUS_LABELS.resolved, value: k.resolved, color: STATUS_COLORS.resolved },
    { name: STATUS_LABELS.escalated, value: k.escalated, color: STATUS_COLORS.escalated },
  ].filter(p => p.value > 0);

  return (
    <div className="space-y-4" ref={printRef}>
      <div className="bg-gradient-to-r from-purple-50 to-fuchsia-50 border border-purple-200 rounded-xl p-3 text-sm text-purple-800 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span className="truncate">لوحة تحليلية لمتابعة إجراءات الاستلامات المتأخرة — كشف تلقائي للاستلامات التي تجاوزت <strong>{data.settings?.overdue_threshold_days || 5}</strong> يوم من تاريخ الاعتماد.</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <button onClick={handleForceRefresh} disabled={refreshing} data-testid="refresh-actions" title="إعادة فحص جميع الاستلامات الآن" className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-emerald-300 hover:bg-emerald-50 disabled:opacity-60 text-emerald-700 rounded-lg text-xs font-bold transition">
              {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              تحديث الآن
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setShowSettings(true)} data-testid="open-settings" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 rounded-lg text-xs font-bold transition">
              <Settings className="w-4 h-4" /> الإعدادات
            </button>
          )}
          <button onClick={handlePrint} data-testid="print-actions-report" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-purple-300 hover:bg-purple-100 text-purple-700 rounded-lg text-xs font-bold transition">
            <Printer className="w-4 h-4" /> طباعة PDF
          </button>
        </div>
      </div>

      {showSettings && (
        <SettingsModal
          token={token}
          current={data.settings?.overdue_threshold_days || 5}
          onClose={() => setShowSettings(false)}
          onSaved={() => { setShowSettings(false); setReloadKey(k => k + 1); }}
        />
      )}

      {data.kpis?.skipped_due_to_throttle && data.settings?.last_auto_run_at && (
        <div className="text-[11px] text-gray-500 -mt-2 flex items-center gap-1" data-testid="throttle-info">
          <Clock className="w-3 h-3" />
          آخر فحص تلقائي: {new Date(data.settings.last_auto_run_at).toLocaleString('ar-EG-u-nu-latn', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          {isAdmin && <span className="mr-1">— اضغط &laquo;تحديث الآن&raquo; لإعادة الفحص فوراً</span>}
        </div>
      )}

      {data.kpis?.auto_detected_now > 0 && (
        <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="auto-detected-banner">
          <Sparkles className="w-5 h-5 shrink-0" />
          <span>تم كشف وإنشاء <strong>{data.kpis.auto_detected_now}</strong> إجراء تلقائياً للاستلامات التي تجاوزت {data.settings?.overdue_threshold_days} يوم.</span>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <Kpi label="إجمالي الإجراءات" value={k.total_actions} icon={TrendingUp} color="purple" />
        <Kpi label="معلَّق" value={k.pending} icon={Clock} color="yellow" />
        <Kpi label="قيد المعالجة" value={k.in_progress} icon={Clock} color="blue" />
        <Kpi label="محلول" value={k.resolved} icon={CheckCircle2} color="green" hint={k.auto_resolved ? `منها ${k.auto_resolved} مُحلَّة تلقائياً (ربط بفاتورة)` : null} />
        <Kpi label="مُصعَّد" value={k.escalated} icon={AlertTriangle} color="red" />
        <Kpi label="نسبة الحل" value={k.resolution_rate} suffix="%" icon={TrendingUp} color="indigo" />
        <Kpi label="متوسط زمن الحل" value={k.avg_resolution_days} suffix="يوم" icon={Clock} color="indigo" />
        <Kpi label="متأخر > 7 أيام" value={k.overdue_7_days} icon={AlertTriangle} color="orange" />
      </div>

      {/* الرسوم البيانية */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pie: status distribution */}
        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-bold text-sm mb-3 text-gray-700">توزيع الإجراءات حسب الحالة</h3>
          {pieData.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={(e) => `${e.name}: ${e.value}`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-gray-400 py-12 text-sm">لا توجد إجراءات بعد</p>}
        </div>
        {/* Bar: top suppliers */}
        <div className="bg-white border rounded-xl p-4">
          <h3 className="font-bold text-sm mb-3 text-gray-700 flex items-center gap-2"><Building2 className="w-4 h-4" /> أعلى الموردين بإجراءات تأخير</h3>
          {data.top_suppliers?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.top_suppliers.slice(0, 7)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={120} />
                <Tooltip />
                <Bar dataKey="count" fill="#a855f7" name="إجراءات" />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-gray-400 py-12 text-sm">لا توجد بيانات</p>}
        </div>
      </div>

      {/* أعلى المتابعين والحلّالين */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TopList title="أعلى المتابعين (منشئو الإجراءات)" icon={Users} items={data.top_trackers} color="purple" />
        <TopList title="أعلى من قام بحل الإجراءات" icon={Award} items={data.top_resolvers} color="green" />
      </div>

      {/* قائمة الإجراءات مع فلاتر وبحث وعرض/طباعة */}
      <div className="bg-white border rounded-xl overflow-hidden">
        {/* Header with view mode toggle */}
        <div className={`${viewMode === 'resolved' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'} border-b px-4 py-2 flex items-center justify-between gap-2 flex-wrap`}>
          <div className="flex items-center gap-2">
            {viewMode === 'resolved' ? <FileCheck2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-red-600" />}
            <h3 className={`font-bold text-sm ${viewMode === 'resolved' ? 'text-emerald-900' : 'text-red-900'}`}>
              {viewMode === 'resolved' ? `الإجراءات المُحلَّة (استلامات صدرت فواتيرها) — ${filteredList.length}` : `الإجراءات المتأخرة — ${filteredList.length}`}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg overflow-hidden border border-gray-300 text-xs">
              <button onClick={() => setViewMode('overdue')} data-testid="view-overdue" className={`px-3 py-1.5 font-bold transition ${viewMode === 'overdue' ? 'bg-red-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                المتأخرة ({data.overdue_list?.length || 0})
              </button>
              <button onClick={() => setViewMode('resolved')} data-testid="view-resolved" className={`px-3 py-1.5 font-bold transition ${viewMode === 'resolved' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                المُحلَّة ({data.resolved_list?.length || 0})
              </button>
            </div>
          </div>
        </div>

        {/* Filters bar */}
        <div className="bg-gray-50 border-b px-3 py-2 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="بحث برقم الاستلام/المورد/الملاحظات..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="actions-search"
              className="w-full h-9 pr-8 pl-2 border rounded-md text-sm bg-white outline-none focus:border-purple-400"
            />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="actions-status-filter" className="h-9 px-2 border rounded-md text-sm bg-white outline-none focus:border-purple-400">
            <option value="all">كل الحالات</option>
            <option value="pending">معلَّق</option>
            <option value="in_progress">قيد المعالجة</option>
            <option value="resolved">محلول</option>
            <option value="escalated">مُصعَّد</option>
          </select>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} data-testid="actions-source-filter" className="h-9 px-2 border rounded-md text-sm bg-white outline-none focus:border-purple-400">
            <option value="all">كل المصادر</option>
            <option value="auto">تلقائي فقط</option>
            <option value="manual">يدوي فقط</option>
          </select>
          <input
            type="number"
            min="0"
            placeholder="تأخير ≥ يوم"
            value={minDays}
            onChange={e => setMinDays(e.target.value)}
            data-testid="actions-min-days"
            className="h-9 w-28 px-2 border rounded-md text-sm bg-white outline-none focus:border-purple-400"
          />
          {(search || statusFilter !== 'all' || sourceFilter !== 'all' || minDays) && (
            <button onClick={() => { setSearch(''); setStatusFilter('all'); setSourceFilter('all'); setMinDays(''); }} data-testid="clear-filters" className="inline-flex items-center gap-1 px-2 h-9 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md border border-gray-200">
              <X className="w-3.5 h-3.5" /> مسح
            </button>
          )}
          <div className="text-xs text-gray-500 flex items-center gap-1 mr-auto">
            <Filter className="w-3 h-3" /> {filteredList.length} من {viewMode === 'resolved' ? (data.resolved_list?.length || 0) : (data.overdue_list?.length || 0)}
          </div>
        </div>

        {filteredList.length ? (
          <div className="w-full">
            <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '3%' }} />
                <col style={{ width: viewMode === 'resolved' ? '12%' : '13%' }} />
                <col style={{ width: viewMode === 'resolved' ? '12%' : '13%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: viewMode === 'resolved' ? '17%' : '23%' }} />
                {viewMode === 'resolved' && <col style={{ width: '10%' }} />}
                <col style={{ width: viewMode === 'resolved' ? '18%' : '20%' }} />
                <col style={{ width: '4%' }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-1.5 py-2 text-right text-xs font-bold text-gray-600">#</th>
                  <th className="px-1.5 py-2 text-right text-xs font-bold text-gray-600">رقم الاستلام</th>
                  <th className="px-1.5 py-2 text-right text-xs font-bold text-gray-600">المورد</th>
                  <th className="px-1.5 py-2 text-right text-xs font-bold text-gray-600">الحالة</th>
                  <th className="px-1.5 py-2 text-right text-xs font-bold text-gray-600">المتابع</th>
                  <th className="px-1.5 py-2 text-center text-xs font-bold text-gray-600">أيام التأخير</th>
                  <th className="px-1.5 py-2 text-right text-xs font-bold text-gray-600">ملاحظات</th>
                  {viewMode === 'resolved' && <th className="px-1.5 py-2 text-right text-xs font-bold text-gray-600">رقم الفاتورة</th>}
                  <th className="px-1.5 py-2 text-right text-xs font-bold text-gray-600">آخر بند Timeline</th>
                  <th className="px-1.5 py-2 text-center text-xs font-bold text-gray-600">طباعة</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((it, i) => {
                  const days = it.days_delayed != null ? it.days_delayed : it.days_open;
                  const dayCls = days == null ? 'bg-gray-200 text-gray-600' : days >= 14 ? 'bg-red-600 text-white' : days >= 7 ? 'bg-orange-500 text-white' : 'bg-yellow-300 text-yellow-900';
                  const lh = it.last_history;
                  const isAutoCreator = (it.created_by_name || '').includes('النظام');
                  return (
                    <tr key={i} className="border-t hover:bg-gray-50 align-middle">
                      <td className="px-1.5 py-2 text-gray-500 text-xs">{i + 1}</td>
                      <td className="px-1.5 py-2 font-semibold text-xs">
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <button
                            type="button"
                            onClick={() => setDeliveryModal({ delivery_id: it.delivery_id, receipt_number: it.receipt_number })}
                            data-testid={`open-delivery-${i}`}
                            title="انقر لعرض تفاصيل كشف الاستلام والمشروع"
                            className="truncate text-purple-700 hover:text-purple-900 hover:underline text-right block w-full"
                          >
                            {it.receipt_number}
                          </button>
                          <div className="flex items-center gap-1 flex-wrap">
                            {it.auto_created && (
                              <span title="أُنشئ تلقائياً بواسطة النظام" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[9px] font-bold">
                                <Sparkles className="w-2.5 h-2.5" /> تلقائي
                              </span>
                            )}
                            {it.auto_resolved_by_linking && (
                              <span title="حُلّ بربط الاستلام بفاتورة" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-[9px] font-bold">
                                <FileCheck2 className="w-2.5 h-2.5" /> ربط
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-1.5 py-2 text-xs">
                        <div className="line-clamp-2 leading-tight break-words" title={it.supplier_name || '—'}>
                          {it.supplier_name || '—'}
                        </div>
                      </td>
                      <td className="px-1.5 py-2"><span className="text-xs font-bold whitespace-nowrap" style={{ color: STATUS_COLORS[it.status] }}>{STATUS_LABELS[it.status]}</span></td>
                      <td className="px-1.5 py-2 text-xs">
                        {isAutoCreator ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full font-bold whitespace-nowrap" title={it.created_by_name}>
                            <Sparkles className="w-3 h-3" /> النظام
                          </span>
                        ) : (
                          <div className="text-purple-700 truncate" title={it.created_by_name || '—'}>{it.created_by_name || '—'}</div>
                        )}
                      </td>
                      <td className="px-1.5 py-2 text-center">
                        <span className={`inline-flex items-center justify-center min-w-[2.25rem] px-2 py-0.5 rounded-full text-xs font-bold ${dayCls}`}>{days != null ? days : '—'}</span>
                      </td>
                      <td className="px-1.5 py-2 text-gray-600 text-xs">
                        <div className="line-clamp-2 leading-tight break-words" title={it.notes || '—'}>{it.notes || '—'}</div>
                      </td>
                      {viewMode === 'resolved' && (
                        <td className="px-1.5 py-2 text-xs">
                          {(it.linked_invoices || []).length > 0 ? (
                            <div className="flex flex-col gap-0.5">
                              {(it.linked_invoices || []).slice(0, 2).map((li, j) => (
                                <span key={j} className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded font-bold text-[10px] truncate">
                                  #{li.invoice_number || '—'}
                                </span>
                              ))}
                              {(it.linked_invoices || []).length > 2 && <span className="text-gray-400 text-[10px]">+{it.linked_invoices.length - 2}</span>}
                            </div>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                      )}
                      <td className="px-1.5 py-2 text-xs" data-testid={`last-history-${i}`}>
                        {lh ? (
                          <div>
                            <div className="text-gray-800 line-clamp-2 leading-tight break-words" title={lh.text}>{lh.text || '—'}</div>
                            <div className="text-gray-400 text-[10px] mt-0.5 truncate">{lh.by} — {String(lh.at || '').slice(0, 16).replace('T', ' ')}</div>
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-1.5 py-2 text-center">
                        <button onClick={() => printSingleAction(it)} data-testid={`print-row-${i}`} title="طباعة الإجراء بكامل تفاصيله وسجل Timeline" className="inline-flex items-center justify-center w-8 h-8 rounded-md text-purple-600 hover:bg-purple-100 transition">
                          <Printer className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : <p className="text-center text-gray-400 py-8 text-sm">
          {viewMode === 'resolved' ? 'لا توجد إجراءات محلولة تطابق الفلاتر' : 'لا توجد إجراءات متأخرة تطابق الفلاتر 🎉'}
        </p>}
      </div>
      
      {deliveryModal && (
        <DeliveryDetailsModal
          token={token}
          deliveryId={deliveryModal.delivery_id}
          highlightReceipt={deliveryModal.receipt_number}
          onClose={() => setDeliveryModal(null)}
        />
      )}
    </div>
  );
}

export function DeliveryDetailsModal({ token, deliveryId, highlightReceipt, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${API_URL}/api/invoice-matching/delivery-details/${deliveryId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, deliveryId]);
  
  const STATUS_LBL = { draft: 'مسودة', pending_approval: 'بانتظار الاعتماد', approved: 'معتمَد', rejected: 'مرفوض' };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="delivery-details-modal">
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 flex items-center justify-between shrink-0">
          <h3 className="font-bold flex items-center gap-2"><FileCheck2 className="w-5 h-5" /> تفاصيل كشف الاستلام</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" /></div>
          ) : !data ? (
            <p className="text-center text-red-500 py-8">تعذّر تحميل تفاصيل الكشف</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div className="border rounded-lg p-2 bg-gray-50">
                  <div className="text-[11px] text-gray-500">رقم الكشف</div>
                  <div className="font-bold text-purple-700">#{data.delivery_number || '—'}</div>
                </div>
                <div className="border rounded-lg p-2 bg-amber-50">
                  <div className="text-[11px] text-gray-500">المشروع</div>
                  <div className="font-bold text-amber-800">{data.project_number || data.project_name || '— غير محدد —'}</div>
                </div>
                <div className="border rounded-lg p-2 bg-green-50">
                  <div className="text-[11px] text-gray-500">الحالة</div>
                  <div className="font-bold text-green-700">{STATUS_LBL[data.status] || data.status || '—'}</div>
                </div>
                <div className="border rounded-lg p-2">
                  <div className="text-[11px] text-gray-500">تاريخ الإنشاء</div>
                  <div className="font-bold">{String(data.created_at || '').slice(0, 10) || '—'}</div>
                </div>
                <div className="border rounded-lg p-2">
                  <div className="text-[11px] text-gray-500">المنشئ</div>
                  <div className="font-bold">{data.created_by || '—'}</div>
                </div>
                <div className="border rounded-lg p-2">
                  <div className="text-[11px] text-gray-500">تاريخ الاعتماد</div>
                  <div className="font-bold text-purple-700">{String(data.approved_at || '').slice(0, 10) || '—'}</div>
                </div>
                <div className="border rounded-lg p-2 col-span-2 sm:col-span-3">
                  <div className="text-[11px] text-gray-500">المعتمِد</div>
                  <div className="font-bold">{data.approved_by || '—'}</div>
                </div>
              </div>
              
              {data.notes && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                  <div className="text-[11px] text-gray-600 font-bold mb-1">ملاحظات الكشف:</div>
                  <div className="text-gray-800 whitespace-pre-wrap">{data.notes}</div>
                </div>
              )}
              
              <div>
                <h4 className="font-bold text-sm text-gray-700 mb-2">الموردون والاستلامات ({(data.suppliers || []).length})</h4>
                {(data.suppliers || []).map((sup, si) => (
                  <div key={si} className="border rounded-lg mb-2">
                    <div className="bg-purple-50 px-3 py-2 font-bold text-sm text-purple-900 border-b">
                      {sup.supplier_name || 'بدون اسم'}
                      <span className="text-xs text-gray-500 font-normal mr-2">— {(sup.receipts || []).length} استلام</span>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="px-2 py-1.5 text-right">#</th>
                          <th className="px-2 py-1.5 text-right">رقم الاستلام</th>
                          <th className="px-2 py-1.5 text-right">التاريخ</th>
                          <th className="px-2 py-1.5 text-right">المشروع</th>
                          <th className="px-2 py-1.5 text-right">فواتير مربوطة</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(sup.receipts || []).map((rec, ri) => {
                          const isHighlighted = rec.receipt_number === highlightReceipt;
                          const li = rec.linked_invoices || [];
                          return (
                            <tr key={ri} className={`border-t ${isHighlighted ? 'bg-yellow-100 font-bold' : ''}`}>
                              <td className="px-2 py-1.5 text-gray-500">{ri + 1}</td>
                              <td className="px-2 py-1.5">{rec.receipt_number || '—'}{isHighlighted && <span className="mr-1 text-purple-700">◄</span>}</td>
                              <td className="px-2 py-1.5">{rec.receipt_date || '—'}</td>
                              <td className="px-2 py-1.5">{rec.project_number || '—'}</td>
                              <td className="px-2 py-1.5">
                                {li.length > 0 ? (
                                  <span className="inline-flex items-center gap-1 text-emerald-700">
                                    {li.map(l => l.invoice_number).filter(Boolean).join(' / ') || `${li.length} فاتورة`}
                                  </span>
                                ) : <span className="text-amber-700">بدون فاتورة</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="border-t p-3 flex justify-end shrink-0">
          <button onClick={onClose} className="px-4 py-2 border rounded-lg text-sm font-medium hover:bg-gray-50">إغلاق</button>
        </div>
      </div>
    </div>
  );
}
